import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CageDateProvider } from './contexts/CageDateContext';
import { Wallet } from 'lucide-react';
import Layout from './components/Layout';
import ChipBankLayout from './components/ChipBankLayout';
import PosLayout from './components/PosLayout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Users from './pages/Users';
import Games from './pages/Games';
import Settings from './pages/Settings';
import Notifications from './pages/Notifications';
import ActivityLog from './pages/ActivityLog';
import Admins from './pages/Admins';
import TableManager from './pages/TableManager';
import Achievements from './pages/Achievements';
import CasinoInfo from './pages/CasinoInfo';
import Rewards from './pages/Rewards';
import Randomizer from './pages/Randomizer';
import SessionCorrections from './pages/SessionCorrections';
import PosRegister from './pages/PosRegister';
import PosKitchen from './pages/PosKitchen';
import PosOrders from './pages/PosOrders';
import PosMenuManager from './pages/PosMenuManager';
import PosReports from './pages/PosReports';
import StaffManagement from './pages/StaffManagement';
import Beacons from './pages/Beacons';
import Leaderboards from './pages/Leaderboards';
import SocialRewards from './pages/SocialRewards';
import RedemptionLog from './pages/RedemptionLog';
import Reports from './pages/Reports';
import StatusSystem from './pages/StatusSystem';
import JobsTesting from './pages/JobsTesting';
import DevTools from './pages/DevTools';
import StaffReferrals from './pages/StaffReferrals';
import Cage from './pages/Cage';
import Disputes from './pages/Disputes';
import Reconciliation from './pages/Reconciliation';
import LedgerReport from './pages/LedgerReport';
import CreditAging from './pages/CreditAging';
import Restrictions from './pages/Restrictions';
import CageBalanceSheet from './pages/CageBalanceSheet';
import CageGlMapping from './pages/CageGlMapping';
import CageDropCount from './pages/CageDropCount';
import CageFreePlay from './pages/CageFreePlay';
import CageChipLiability from './pages/CageChipLiability';
import CageReports from './pages/CageReports';
import CageMtl from './pages/CageMtl';
import Surveillance from './pages/Surveillance';
import TournamentPrepays from './pages/TournamentPrepays';
import ReviewHub from './pages/ReviewHub';
import Waitlist from './pages/Waitlist';
import Roles from './pages/Roles';
import PlayerBankSettings from './pages/PlayerBankSettings';
import AdManager from './pages/AdManager';
import Features from './pages/Features';
import NotificationTemplates from './pages/NotificationTemplates';
import Content from './pages/Content';
import Protected from './components/Protected';
import VersionBanner from './components/VersionBanner';

// Build variant. The default `vite build` produces the full admin
// dashboard ('admin'); `npm run build:chipbank` sets VITE_APP_VARIANT
// and produces the standalone Chip Bank dashboard ('chipbank'), which
// is deployed to its own Firebase Hosting site. Both variants share
// this single codebase, the same auth, and the same backend API.
const APP_VARIANT = import.meta.env.VITE_APP_VARIANT || 'admin';


function PrivateRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return user ? children : <Navigate to="/login" />;
}

// Component to protect platform-admin-only routes (Radly). Client admins — even
// owners — are redirected; feature enablement is a Radly-only control.
function PlatformAdminRoute({ children }) {
  const { isPlatformAdmin, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500"></div>
      </div>
    );
  }
  return isPlatformAdmin ? children : <Navigate to="/" />;
}

// Component to protect admin-only routes from staff
function AdminOnlyRoute({ children }) {
  const { isAdmin, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  // Staff users get redirected to dashboard
  return isAdmin ? children : <Navigate to="/" />;
}

// ============================================================
// Full admin dashboard routes (APP_VARIANT === 'admin')
// ============================================================
function AdminRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        path="/"
        element={
          <PrivateRoute>
            <Layout />
          </PrivateRoute>
        }
      >
        {/* Permission-gated routes */}
        <Route index element={<Protected permission="dashboard:view"><Dashboard /></Protected>} />
        <Route path="table-manager" element={<Protected permission="tables:view"><TableManager /></Protected>} />
        <Route path="randomizer" element={<Protected permission="tables:view"><Randomizer /></Protected>} />
        <Route path="session-corrections" element={<Protected permission="tables:manage"><SessionCorrections /></Protected>} />
        <Route path="pos" element={<Protected permission="pos:operate"><PosRegister /></Protected>} />
        <Route path="pos-kitchen" element={<Protected permission="pos:kitchen"><PosKitchen /></Protected>} />
        <Route path="pos-orders" element={<Protected permission="pos:operate"><PosOrders /></Protected>} />
        <Route path="pos-menu" element={<Protected permission="pos:manage"><PosMenuManager /></Protected>} />
        <Route path="pos-reports" element={<Protected permission="pos:manage"><PosReports /></Protected>} />
        <Route path="reports" element={<Protected permission="reports:view"><Reports /></Protected>} />
        <Route path="staff-referrals" element={<Protected permission="staff_referrals:view"><StaffReferrals /></Protected>} />
        <Route path="beacons" element={<Protected permission="settings:manage"><Beacons /></Protected>} />
        <Route path="leaderboards" element={<Protected permission="settings:manage"><Leaderboards /></Protected>} />
        <Route path="social-rewards" element={<Protected permission="rewards:manage"><SocialRewards /></Protected>} />
        <Route path="cage" element={<Protected permission="cage:view"><Cage /></Protected>} />
        <Route path="credit-aging" element={<Protected permission="credit:view"><CreditAging /></Protected>} />
        <Route path="restrictions" element={<Protected permission="users:edit"><Restrictions /></Protected>} />
        <Route path="cage-balance" element={<Protected permission="cbs:view"><CageBalanceSheet /></Protected>} />
        <Route path="cage-gl-mapping" element={<Protected permission="cbs:configure"><CageGlMapping /></Protected>} />
        <Route path="cage-drop-count" element={<Protected permission="cbs:view"><CageDropCount /></Protected>} />
        <Route path="cage-free-play" element={<Protected permission="cbs:liability:enter"><CageFreePlay /></Protected>} />
        <Route path="cage-chip-liability" element={<Protected permission="cbs:liability:enter"><CageChipLiability /></Protected>} />
        <Route path="cage-reports" element={<Protected permission="cbs:view"><CageReports /></Protected>} />
        <Route path="cage-mtl" element={<Protected permission="title31:view"><CageMtl /></Protected>} />
        <Route path="surveillance" element={<Protected permission="surveillance:view"><Surveillance /></Protected>} />
        <Route path="tournament-prepays" element={<Protected permission="tournaments:prepay:view"><TournamentPrepays /></Protected>} />
        <Route path="waitlist" element={<Protected permission="waitlist:view"><Waitlist /></Protected>} />
        <Route path="disputes" element={<Protected permission="disputes:view"><Disputes /></Protected>} />
        <Route path="reconciliation" element={<Protected permission="reconciliation:view"><Reconciliation /></Protected>} />
        <Route path="ledger" element={<Protected permission="ledger:view"><LedgerReport /></Protected>} />
        <Route path="player-bank-settings" element={<Protected permission="settings:manage"><PlayerBankSettings /></Protected>} />
        <Route path="users" element={<Protected permission="users:view"><Users /></Protected>} />
        <Route path="games" element={<Protected permission="games:view"><Games /></Protected>} />
        <Route path="casino-info" element={<Protected permission="casino_info:manage"><CasinoInfo /></Protected>} />
        <Route path="settings" element={<Protected permission="settings:manage"><Settings /></Protected>} />
        <Route path="features" element={<PlatformAdminRoute><Features /></PlatformAdminRoute>} />
        <Route path="content" element={<Protected permission="settings:manage"><Content /></Protected>} />
        <Route path="status-system" element={<Protected permission="status_system:manage"><StatusSystem /></Protected>} />
        <Route path="achievements" element={<Protected permission="achievements:manage"><Achievements /></Protected>} />
        <Route path="rewards" element={<Protected permission="rewards:manage"><Rewards /></Protected>} />
        <Route path="redemptions" element={<Protected permission="redemptions:view"><RedemptionLog /></Protected>} />
        <Route path="review-hub" element={<Protected permission="review_hub:manage"><ReviewHub /></Protected>} />
        <Route path="ads" element={<Protected permission="ads:view"><AdManager /></Protected>} />
        <Route path="staff" element={<Protected permission="staff:manage"><StaffManagement /></Protected>} />
        <Route path="notifications" element={<Protected permission="notifications:view"><Notifications /></Protected>} />
        <Route path="notification-templates" element={<Protected permission="notifications:send"><NotificationTemplates /></Protected>} />
        <Route path="activity" element={<Protected permission="activity_log:view"><ActivityLog /></Protected>} />
        <Route path="admins" element={<Protected permission="admins:view"><Admins /></Protected>} />
        <Route path="roles" element={<Protected permission="admins:manage"><Roles /></Protected>} />
        <Route path="jobs" element={<Protected permission="jobs:run"><JobsTesting /></Protected>} />
        <Route path="dev-tools" element={<Protected permission="dev_tools:access"><DevTools /></Protected>} />
      </Route>
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

// ============================================================
// Standalone Chip Bank dashboard (APP_VARIANT === 'chipbank')
// ============================================================
// Index resolver: send the player to the first Chip Bank tab they are
// permitted to see, or to the no-access page if they have none. This
// also keeps Protected's "/" fallback from looping.
function ChipBankHome() {
  const { can } = useAuth();
  if (can('cage:view')) return <Navigate to="/cage" replace />;
  if (can('disputes:view')) return <Navigate to="/disputes" replace />;
  if (can('reconciliation:view')) return <Navigate to="/reconciliation" replace />;
  if (can('ledger:view')) return <Navigate to="/ledger" replace />;
  return <Navigate to="/no-access" replace />;
}

// Shown when a signed-in user has none of the Chip Bank permissions.
function ChipBankNoAccess() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
      <div className="text-center max-w-md">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-800 rounded-2xl mb-4">
          <Wallet className="w-8 h-8 text-gray-500" />
        </div>
        <h1 className="text-xl font-bold text-white">No Player Bank access</h1>
        <p className="text-gray-400 mt-2">
          {user?.email ? `Your account (${user.email})` : 'Your account'} doesn&apos;t
          have permission to use the Player Bank. Contact an administrator if you
          think this is a mistake.
        </p>
        <button
          onClick={() => {
            logout();
            navigate('/login');
          }}
          className="mt-6 px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg border border-gray-700"
        >
          Sign out
        </button>
      </div>
    </div>
  );
}

function ChipBankRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/no-access" element={<ChipBankNoAccess />} />
      <Route
        path="/"
        element={
          <PrivateRoute>
            <ChipBankLayout />
          </PrivateRoute>
        }
      >
        <Route index element={<ChipBankHome />} />
        <Route path="cage" element={<Protected permission="cage:view"><Cage /></Protected>} />
        <Route path="credit-aging" element={<Protected permission="credit:view"><CreditAging /></Protected>} />
        <Route path="cage-balance" element={<Protected permission="cbs:view"><CageBalanceSheet /></Protected>} />
        <Route path="cage-gl-mapping" element={<Protected permission="cbs:configure"><CageGlMapping /></Protected>} />
        <Route path="cage-drop-count" element={<Protected permission="cbs:view"><CageDropCount /></Protected>} />
        <Route path="cage-free-play" element={<Protected permission="cbs:liability:enter"><CageFreePlay /></Protected>} />
        <Route path="cage-chip-liability" element={<Protected permission="cbs:liability:enter"><CageChipLiability /></Protected>} />
        <Route path="cage-reports" element={<Protected permission="cbs:view"><CageReports /></Protected>} />
        <Route path="cage-mtl" element={<Protected permission="title31:view"><CageMtl /></Protected>} />
        <Route path="surveillance" element={<Protected permission="surveillance:view"><Surveillance /></Protected>} />
        <Route path="tournament-prepays" element={<Protected permission="tournaments:prepay:view"><TournamentPrepays /></Protected>} />
        <Route path="disputes" element={<Protected permission="disputes:view"><Disputes /></Protected>} />
        <Route path="reconciliation" element={<Protected permission="reconciliation:view"><Reconciliation /></Protected>} />
        <Route path="ledger" element={<Protected permission="ledger:view"><LedgerReport /></Protected>} />
        <Route path="player-bank-settings" element={<Protected permission="settings:manage"><PlayerBankSettings /></Protected>} />
      </Route>
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

// Standalone POS site (APP_VARIANT === 'pos') — tablet register + KDS at its own URL.
function PosRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/" element={<PrivateRoute><PosLayout /></PrivateRoute>}>
        <Route index element={<Navigate to="/register" replace />} />
        <Route path="register" element={<Protected permission="pos:operate"><PosRegister /></Protected>} />
        <Route path="kitchen" element={<Protected permission="pos:kitchen"><PosKitchen /></Protected>} />
        <Route path="orders" element={<Protected permission="pos:operate"><PosOrders /></Protected>} />
        <Route path="menu" element={<Protected permission="pos:manage"><PosMenuManager /></Protected>} />
        <Route path="reports" element={<Protected permission="pos:manage"><PosReports /></Protected>} />
      </Route>
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        {/* One business date for every Cage Balancing tab. Sits above the
            router so it survives navigation between the cage pages. */}
        <CageDateProvider>
          <VersionBanner />
          {APP_VARIANT === 'chipbank' ? <ChipBankRoutes /> : APP_VARIANT === 'pos' ? <PosRoutes /> : <AdminRoutes />}
        </CageDateProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
